import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, arrayUnion, arrayRemove, getDocs, documentId } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { useAuth } from '../../auth/contexts/AuthContext';
import { hasFullPlatformStaffStatus } from '../../auth/teacherAccess';
import { Squad, User } from '../../../types';
import CreateSquadModal from './CreateSquadModal';
import SquadCalendar from './SquadCalendar';
import SquadMap from './SquadMap';
import TeacherDashboard from './TeacherDashboard';
import TeacherTeam from './TeacherTeam';
import '../styles/Squads.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { toMsSafe } from '../../../shared/utils/firestoreDates';
import LoadingSpinner from '../../../shared/ui/LoadingSpinner';

const SQUADS_PER_PAGE = 16;
type SquadsTab = 'dashboard' | 'calendar' | 'map' | 'squads' | 'team';

function tabFromSearch(search: string): SquadsTab {
  const view = String(new URLSearchParams(search).get('view') || '').toLowerCase();
  return ['calendar', 'map', 'squads', 'team'].includes(view) ? view as SquadsTab : 'dashboard';
}

const SquadTabIcon: React.FC<{ name: 'dashboard' | 'calendar' | 'squads' | 'map' | 'team' }> = ({ name }) => {
  const paths = {
    dashboard: <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M7 3v4M17 3v4M3 10h18" /></>,
    squads: <><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3.5 20a5.5 5.5 0 0 1 11 0M14 15.5a4.5 4.5 0 0 1 6.5 4" /></>,
    map: <><path d="m3 6 6-3 6 3 6-3v15l-6 3-6-3-6 3Z" /><path d="M9 3v15M15 6v15" /></>,
    team: <><circle cx="9" cy="8" r="3.5" /><path d="M3 20a6 6 0 0 1 12 0" /><path d="m15.5 10 2.5 2.5L22.5 8" /></>,
  };

  return (
    <svg className="squads-tab-icon" viewBox="0 0 24 24" aria-hidden="true">
      {paths[name]}
    </svg>
  );
};

const LiveTimeTracker: React.FC<{ startTime: any }> = ({ startTime }) => {
  const [elapsedTime, setElapsedTime] = useState('');

  useEffect(() => {
    const calculateElapsedTime = () => {
      const now = Date.now();
      const start = toMsSafe(startTime);
      if (!start) {
        setElapsedTime('');
        return;
      }
      const diff = now - start;

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      let timeString = '';
      if (hours > 0) timeString += `${hours}h `;
      if (minutes > 0 || hours > 0) timeString += `${minutes}m `;
      timeString += `${seconds}s`;
      
      setElapsedTime(timeString.trim());
    };

    calculateElapsedTime();
    const interval = setInterval(calculateElapsedTime, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  return <>{elapsedTime}</>;
};

const Squads: React.FC = () => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [squads, setSquads] = useState<Squad[]>([]);
  const [teachers, setTeachers] = useState<User[]>([]);
  const [locations, setLocations] = useState<{ id: string; name: string; partnerId: string }[]>([]);
  const [favoriteSquadIds, setFavoriteSquadIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<SquadsTab>(() => tabFromSearch(location.search));
  const canViewHomeCalendar = userData?.isAdmin === true;

  const selectTab = (tab: SquadsTab) => {
    setActiveTab(tab);
    navigate(tab === 'dashboard' ? location.pathname : `${location.pathname}?view=${tab}`, { replace: true });
  };

  // Removed unused fetchSquads callback (real-time subscription is set up below)

  // Allow navigation (e.g. Home click) to force the default tab.
  useEffect(() => {
    const st = (location.state || {}) as any;
    const desired = String(st?.squadsTab || '').toLowerCase();
    if (desired === 'dashboard' || desired === 'calendar' || desired === 'map' || desired === 'squads' || desired === 'team') {
      setActiveTab(desired as any);
      // Clear one-time nav state so refreshes don't keep forcing tabs.
      navigate(desired === 'dashboard' ? location.pathname : `${location.pathname}?view=${desired}`, { replace: true, state: null });
    } else {
      setActiveTab(tabFromSearch(location.search));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // The workspace-wide calendar exposes staffing and other squads, so it is an
  // admin surface. Redirect teachers who arrive via an old URL or saved state.
  useEffect(() => {
    if (!userData || canViewHomeCalendar || activeTab !== 'calendar') return;
    setActiveTab('dashboard');
    navigate(location.pathname, { replace: true, state: null });
  }, [activeTab, canViewHomeCalendar, location.pathname, navigate, userData]);

  useEffect(() => {
    if (!userData) return;

    const fetchLocations = async () => {
      try {
        const locationSnapshot = await getDocs(query(collection(db, 'locations')));
        setLocations(locationSnapshot.docs.map(item => ({
          id: item.id,
          name: String(item.data().name || item.id),
          partnerId: String(item.data().partnerId || ''),
        })).filter(item => item.partnerId));
      } catch (e) {
        console.error('Failed to fetch locations:', e);
        setLocations([]);
      }
    };

    fetchLocations();

    const q = userData.isAdmin || !currentUser
      ? query(collection(db, 'squads'))
      : query(collection(db, 'squads'), where('teacherIds', 'array-contains', currentUser.uid));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const squadsData = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Squad));
      setSquads(squadsData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching squads:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser, userData]);

  // Load a minimal "teacher directory" only for teachers referenced by squads.
  // Querying all teachers (`where role == 'teacher'`) can be denied under rules due to per-document
  // rule evaluation costs on large collections. This keeps reads small and predictable.
  useEffect(() => {
    const load = async () => {
      try {
        // Firestore rules intentionally prevent non-active teachers from reading other users.
        // If a teacher is in an applicant/offboarded state, skip this optional directory lookup
        // to avoid permission-denied noise and keep the dashboard usable.
        const staffStatus = (userData as any)?.staffStatus;
        const canReadTeacherDirectory = !!(
          userData?.isAdmin === true ||
          (userData?.role === 'teacher' && hasFullPlatformStaffStatus(staffStatus))
        );
        if (!canReadTeacherDirectory) {
          setTeachers([]);
          return;
        }

        const idSet = new Set<string>();
        squads.forEach((s) => {
          (s.teacherIds || []).forEach((tid) => idSet.add(String(tid)));
          const regular = (s as any)?.regularTeacher || (s as any)?.leadTeacherId;
          if (regular) idSet.add(String(regular));
        });

        const ids = Array.from(idSet).filter(Boolean);
        if (ids.length === 0) {
          setTeachers([]);
          return;
        }

        const out: User[] = [];
        for (let i = 0; i < ids.length; i += 10) {
          const group = ids.slice(i, i + 10);
          const snap = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', group)));
          snap.docs.forEach((d) => out.push({ ...(d.data() as any), id: d.id } as User));
        }

        // Stable sort for nicer UX
        out.sort((a, b) => String(a.displayName || '').localeCompare(String(b.displayName || '')));
        setTeachers(out);
      } catch (e) {
        const code = String((e as any)?.code || '').toLowerCase();
        const msg = String((e as any)?.message || '').toLowerCase();
        const isPermDenied = code.includes('permission-denied') || msg.includes('permission-denied');
        if (!isPermDenied) console.error('Failed to load teachers list for squads page:', e);
        setTeachers([]);
      }
    };

    void load();
  }, [squads, userData?.role, (userData as any)?.staffStatus, userData?.isAdmin]);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      setCurrentPage(1); // Reset to first page on search
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const filteredAndSortedSquads = useMemo(() => {
    const filtered = squads.filter(squad =>
      squad.title.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
      squad.code.toLowerCase().includes(debouncedQuery.toLowerCase())
    );
    
    // Sort: archived squads to the bottom, then starred squads first, then by title
    return filtered.sort((a, b) => {
      const aIsArchived = a.status === 'archived';
      const bIsArchived = b.status === 'archived';

      if (aIsArchived && !bIsArchived) return 1;
      if (!aIsArchived && bIsArchived) return -1;

      const aIsStarred = favoriteSquadIds.includes(a.id);
      const bIsStarred = favoriteSquadIds.includes(b.id);
      
      if (aIsStarred && !bIsStarred) return -1;
      if (!aIsStarred && bIsStarred) return 1;
      return a.title.localeCompare(b.title);
    });
  }, [squads, debouncedQuery, favoriteSquadIds]);

  const totalPages = Math.ceil(filteredAndSortedSquads.length / SQUADS_PER_PAGE);
  const currentSquads = filteredAndSortedSquads.slice(
    (currentPage - 1) * SQUADS_PER_PAGE,
    currentPage * SQUADS_PER_PAGE
  );

  const handleSquadCreated = (newSquad: Squad) => {
    setSquads(prevSquads => [...prevSquads, newSquad]);
  };

  const handleSquadUpdated = (updatedSquad: Squad) => {
    setSquads(prevSquads => 
      prevSquads.map(squad => 
        squad.id === updatedSquad.id ? updatedSquad : squad
      )
    );
  };

  const handleToggleFavorite = async (squadId: string) => {
    if (!currentUser) return;
    const userRef = doc(db, 'users', currentUser.uid);
    const isCurrentlyFavorite = favoriteSquadIds.includes(squadId);

    try {
      if (isCurrentlyFavorite) {
        await updateDoc(userRef, { favoriteSquadIds: arrayRemove(squadId) });
        setFavoriteSquadIds(prev => prev.filter(id => id !== squadId));
      } else {
        await updateDoc(userRef, { favoriteSquadIds: arrayUnion(squadId) });
        setFavoriteSquadIds(prev => [...prev, squadId]);
      }
    } catch (error) {
      console.error("Error updating favorites: ", error);
    }
  };

  const handleSquadClick = (squadId: string) => {
    navigate(`/app/squads/${squadId}`);
  };

  const handleInfoClick = (e: React.MouseEvent, squad: Squad) => {
    e.stopPropagation();
    navigate(`/app/squads/${squad.id}/details`);
  };

  if (loading) {
    return <LoadingSpinner fullPage label="Loading your squads…" size="lg" />;
  }

  return (
    <div className="squads-page">
      <div className={`squads-container ${activeTab === 'dashboard' ? 'squads-container--home' : 'squads-container--section'}`}>

        <div className={`squads-tabs ${activeTab === 'dashboard' ? 'squads-tabs--home' : 'squads-tabs--compact'}`} role="tablist" aria-label="Home sections">
          <button
            className={`squads-tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => selectTab('dashboard')}
            role="tab"
            aria-selected={activeTab === 'dashboard'}
            title="Dashboard"
          >
            <SquadTabIcon name="dashboard" />
            <span>Dashboard</span>
          </button>
          {canViewHomeCalendar && (
            <button
              className={`squads-tab-btn ${activeTab === 'calendar' ? 'active' : ''}`}
              onClick={() => selectTab('calendar')}
              role="tab"
              aria-selected={activeTab === 'calendar'}
              title="Calendar"
            >
              <SquadTabIcon name="calendar" />
              <span>Calendar</span>
            </button>
          )}
          <button
            className={`squads-tab-btn ${activeTab === 'squads' ? 'active' : ''}`}
            onClick={() => selectTab('squads')}
            role="tab"
            aria-selected={activeTab === 'squads'}
            title="Squad Search"
          >
            <SquadTabIcon name="squads" />
            <span>Squad Search</span>
          </button>
          <button
            className={`squads-tab-btn ${activeTab === 'team' ? 'active' : ''}`}
            onClick={() => selectTab('team')}
            role="tab"
            aria-selected={activeTab === 'team'}
            title="Team"
          >
            <SquadTabIcon name="team" />
            <span>Team</span>
          </button>
          <button
            className={`squads-tab-btn ${activeTab === 'map' ? 'active' : ''}`}
            onClick={() => selectTab('map')}
            role="tab"
            aria-selected={activeTab === 'map'}
            title="Map"
          >
            <SquadTabIcon name="map" />
            <span>Map</span>
          </button>
        </div>

        {activeTab === 'dashboard' && (
          <div className="calendar-view">
            <TeacherDashboard squads={squads} />
          </div>
        )}

        {canViewHomeCalendar && activeTab === 'calendar' && (
          <div className="calendar-view">
            <SquadCalendar teachers={teachers} squads={squads} />
          </div>
        )}

        {activeTab === 'map' && (
          <div className="map-view">
            <SquadMap />
          </div>
        )}

        {activeTab === 'team' && <TeacherTeam />}

        {activeTab === 'squads' && (
          <>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search squads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="squads-search"
              />
              <span className="search-icon">🔍</span>
            </div>

            {filteredAndSortedSquads.length === 0 && !searchQuery ? (
              <div className="no-squads">
                <div className="no-squads-icon">📚</div>
                <h3>No squads found</h3>
                <p>
                  Ask Aspen or Steven to add you to your squads for this season!
                </p>
              </div>
            ) : filteredAndSortedSquads.length === 0 && searchQuery ? (
              <div className="no-squads">
                <div className="no-squads-icon">📚</div>
                <h3>No squads found</h3>
                <p>
                  No squads match your search. Try a different term.
                </p>
              </div>
            ) : (
              <>
                <div className="squads-list">
                  {currentSquads.map(squad => (
                    <div
                      key={squad.id}
                      className={`squad-row ${squad.classStatus === 'current' ? 'live' : ''}`}
                      onClick={() => handleSquadClick(squad.id)}
                    >
                      <div className="squad-row-content">
                        <div className="squad-left-content">
                          <div className="squad-title-line">
                            <h3 className="squad-title">{squad.title}</h3>
                            <div className="squad-meta">
                              <button
                                className="squad-info-btn"
                                onClick={(e) => handleInfoClick(e, squad)}
                                title="Squad Details"
                              >
                                Details
                              </button>
                              <div className="squad-code">Code: {squad.code}</div>
                            </div>
                          </div>
                          {squad.classStatus === 'current' && squad.liveSession && (
                            <div className="live-info">
                              <span className="live-teacher">
                                Live with {squad.liveSession.teacherName}
                              </span>
                              <span className="live-time">
                                <LiveTimeTracker startTime={squad.liveSession.startTime} />
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="squad-right-content">
                          <div className="squad-stats">
                            <div className="stat">
                              {squad.classStatus === 'current' && squad.attendance ? (
                                <span className="stat-text">
                                  <span className="live-student-count">
                                    {Object.values(squad.attendance).filter(present => present).length}
                                  </span>
                                  /{squad.studentIds?.length || 0} Students
                                </span>
                              ) : (
                                <span className="stat-text">
                                  {squad.studentIds?.length || 0} Students
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="squad-actions">
                            <button
                              className={`star-btn ${favoriteSquadIds.includes(squad.id) ? 'starred' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleFavorite(squad.id);
                              }}
                            >
                              {favoriteSquadIds.includes(squad.id) ? '⭐' : '☆'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      ← Previous
                    </button>

                    <div className="pagination-info">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          className={`pagination-number ${page === currentPage ? 'active' : ''}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <CreateSquadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSquadCreated={handleSquadCreated}
        locations={locations}
      />
    </div>
  );
};

export default Squads;
